import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Calendar,
  Clock,
  MapPin,
  Ticket,
  Download,
  CheckCircle,
  AlertCircle,
  QrCode
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Función para formatear fechas
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export default function TicketPage() {
  const { ticketId } = useParams();
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTicketData();
  }, [ticketId]);

  const fetchTicketData = async () => {
    try {
      const res = await axios.get(`${API_URL}/eventos/ticket/${ticketId}/data`);
      setTicketData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ticket no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    window.open(`${API_URL}/eventos/ticket/${ticketId}/imagen`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <p className="text-gray-600 text-lg mb-4">{error}</p>
        <Link to="/eventos" className="text-violet-600 hover:underline">
          ← Ver eventos
        </Link>
      </div>
    );
  }

  const { evento, nombre_asistente, checked_in, payment_status } = ticketData;
  const isValid = payment_status !== 'pendiente';

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-600 to-violet-800 p-4">
      <div className="max-w-md mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-white text-2xl font-bold flex items-center justify-center gap-2">
            <Ticket className="w-6 h-6" />
            Tu Entrada
          </h1>
        </div>

        {/* Ticket Card */}
        <Card className="overflow-hidden shadow-2xl">
          {/* Header del ticket */}
          <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white p-4">
            <p className="text-xs uppercase tracking-wider opacity-80">Colegio de Trabajadorxs Sociales</p>
            <p className="text-xs opacity-60">Distrito Lomas de Zamora</p>
          </div>

          <CardContent className="p-0">
            {/* Info del evento */}
            <div className="p-6 bg-white">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{evento.nombre}</h2>
              
              <div className="space-y-3 text-sm">
                {evento.fecha_evento && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Calendar className="w-5 h-5 text-violet-500" />
                    <span>{formatDate(evento.fecha_evento)}</span>
                  </div>
                )}
                {evento.hora_evento && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Clock className="w-5 h-5 text-violet-500" />
                    <span>{evento.hora_evento} hs</span>
                  </div>
                )}
                {evento.lugar && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin className="w-5 h-5 text-violet-500" />
                    <span>{evento.lugar}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Línea de corte decorativa */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dashed border-gray-300"></div>
              </div>
              <div className="absolute left-0 -ml-3 w-6 h-6 bg-violet-700 rounded-full"></div>
              <div className="absolute right-0 -mr-3 w-6 h-6 bg-violet-700 rounded-full"></div>
            </div>

            {/* QR Code y datos del asistente */}
            <div className="p-6 bg-gray-50">
              {/* Estado del ticket */}
              <div className="flex justify-center mb-4">
                {checked_in ? (
                  <Badge className="bg-blue-100 text-blue-700 text-sm py-1">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Ya ingresaste
                  </Badge>
                ) : isValid ? (
                  <Badge className="bg-green-100 text-green-700 text-sm py-1">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Entrada válida
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 text-sm py-1">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Pago pendiente
                  </Badge>
                )}
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-4">
                <div className="bg-white p-3 rounded-lg shadow-inner border-2 border-gray-200">
                  <img 
                    src={`${API_URL}/eventos/ticket/${ticketId}/imagen`}
                    alt="QR Code"
                    className="w-40 h-40"
                    data-testid="ticket-qr-code"
                  />
                </div>
              </div>

              {/* Nombre del asistente */}
              <div className="text-center mb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Asistente</p>
                <p className="text-lg font-semibold text-gray-900">{nombre_asistente}</p>
              </div>

              {/* Código del ticket */}
              <div className="text-center bg-white rounded-lg py-3 px-4 border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Código de entrada</p>
                <p className="text-2xl font-mono font-bold text-violet-600 tracking-widest">
                  {ticketData.ticket_id}
                </p>
              </div>
            </div>

            {/* Acciones */}
            <div className="p-4 bg-white border-t">
              <Button 
                onClick={handleDownloadQR}
                className="w-full bg-violet-600 hover:bg-violet-700"
                data-testid="btn-download-qr"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar QR
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Instrucciones */}
        <div className="mt-6 text-center text-white/80 text-sm">
          <p>Presentá este código QR en la entrada del evento</p>
          <p className="mt-2">
            <Link to="/eventos" className="underline hover:text-white">
              Ver más eventos
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
